import React from 'react';

import type { IntlShape, MessageDescriptor } from 'react-intl';

import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import type { MarkType, NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ListBulletedIcon } from '@atlaskit/editor-toolbar';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import ChildWorkItemsIcon from '@atlaskit/icon/core/child-work-items';
import ClockIcon from '@atlaskit/icon/core/clock';
import DecisionIcon from '@atlaskit/icon/core/decision';
import ListChecklistIcon from '@atlaskit/icon/core/list-checklist';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import type { JsonLd } from '@atlaskit/json-ld-types';
import type { SmartLinkResponse } from '@atlaskit/linking-types';

import { createOpenAIModalCommand } from '../../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../../../types/types';
import {
	smartLinkDraftContent,
	type DraftContentActionType,
} from '../../prebuilt/config-items/prompts/smartlink-draft-content/smartlink-draft-content';
import {
	smartLinkExtractActions,
	type ExtractActionsActionType,
} from '../../prebuilt/config-items/prompts/smartlink-extract-actions/smartlink-extract-actions';
import {
	smartLinkExtractList,
	type ExtractListActionType,
} from '../../prebuilt/config-items/prompts/smartlink-extract-list/smartlink-extract-list';

// Union of all action types defined in the config-item prompt files.
// 'extract_key_insights' and 'draft_release_notes' are excluded because they have no
// corresponding action defined in getActionsForSmartLink() yet.
// Removing an Exclude will surface a compile error pointing exactly where the missing label and icon need to be added.
export type SmartLinkActionKey =
	| Exclude<ExtractListActionType, 'extract_key_insights'>
	| ExtractActionsActionType
	| Exclude<DraftContentActionType, 'draft_release_notes'>;

export type SmartLinkDropdownItem = {
	icon: React.ReactNode;
	label: string;
	onClick: () => void;
	testId: string;
};

const actionLabels = {
	list_key_topics: aiMessages.smartlinkListKeyTopics,
	list_key_themes: aiMessages.smartlinkListKeyThemes,
	extract_insights: aiMessages.smartlinkExtractInsights,
	extract_user_quotes: aiMessages.smartlinkExtractUserQuotes,
	add_decisions: aiMessages.smartlinkAddDecisions,
	define_action_items: aiMessages.smartlinkDefineActionItems,
	summarize_changes: aiMessages.smartlinkSummarizeChanges,
	add_related_work_items: aiMessages.smartlinkAddRelatedWorkItems,
	list_todos: aiMessages.smartlinkListTodos,
} satisfies Record<SmartLinkActionKey, MessageDescriptor>;

/**
 * Determines which AI actions are available for a given SmartLink based on its provider
 * and content type.
 *
 * using similar detection strategy as server-side rendering of actions in SmartLinkCard,
 * which is based on provider and content type metadata returned by the Smart Link Resolver.
 * see `platform/packages/linking-platform/smart-card/src/state/helpers.ts`
 *
 * Detection strategy:
 * - `details.meta.key` is the primary discriminator for all known providers.
 * - `details.meta.resourceType` differentiates Confluence sub-types (page, whiteboard, database).
 * - `details.data['@type']` differentiates Google Workspace sub-types (Docs, Sheets, Slides).
 * - `details.data['schema:fileFormat']` (MIME type) differentiates Microsoft 365 sub-types.
 * - `details.data.generator.name` is the fallback for providers without a confirmed meta.key
 *   in the frontend codebase (Zoom, MS Teams, Google Chat, Mural, Notion, Smartsheet).
 *
 * Returns an empty array for unknown or unsupported providers
 */
export const getActionsForSmartLink = (details?: SmartLinkResponse): SmartLinkActionKey[] => {
	// `meta` has a [k: string]: any index signature, so direct access is safe without casting.
	const metaKey = details?.meta?.key as string | undefined;
	const resourceType = details?.meta?.resourceType as string | undefined;
	const metaProduct = details?.meta?.product as string | undefined;

	// Cast to BaseData (the common base for non-collection responses) to access typed properties.
	const data = details?.data as JsonLd.Data.BaseData | undefined;
	const dataType = data?.['@type'];
	// schema:fileFormat only exists on Document, which extends BaseData.
	const fileFormat = (data as JsonLd.Data.Document | undefined)?.['schema:fileFormat'];
	// generator is typed as Primitives.Object | Primitives.Link on BaseData.
	// Primitives.Link can be a string or LinkModel (which has href). Only Object has name.
	const generator = data?.generator;
	const generatorName =
		generator && typeof generator !== 'string' && !('href' in generator)
			? generator.name
			: undefined;

	const hasType = (type: JsonLd.Primitives.ObjectType): boolean =>
		Array.isArray(dataType) ? dataType.includes(type) : dataType === type;

	// all Confluence content types share meta.product === 'confluence'
	if (metaProduct === 'confluence') {
		if (resourceType === 'whiteboard') {
			return ['list_key_themes', 'add_decisions', 'define_action_items'];
		}
		if (resourceType === 'database') {
			return ['extract_insights'];
		}
		// page, live-page, blog, and any other Confluence page-like resource types
		return ['summarize_changes', 'list_key_topics'];
	}

	switch (metaKey) {
		case 'jira-object-provider':
			return ['add_related_work_items'];

		case 'loom-object-provider':
			return ['extract_user_quotes', 'add_decisions', 'define_action_items'];

		case 'trello-object-provider':
			return ['list_todos'];

		case 'miro-object-provider':
			return ['list_key_themes'];

		case 'google-object-provider':
			if (hasType('schema:SpreadsheetDigitalDocument')) {
				return ['extract_insights'];
			}
			// TextDigitalDocument (Google Docs) and other Google doc types
			return ['list_key_topics'];

		case 'onedrive-object-provider':
		case 'sharepoint-object-provider':
			if (fileFormat?.includes('spreadsheetml') || fileFormat?.includes('ms-excel')) {
				return ['extract_insights'];
			}
			// wordprocessingml, ms-word, presentationml, and other Office file types
			return ['list_key_topics'];

		case 'figma-object-provider':
			return ['summarize_changes'];

		case 'slack-object-provider':
			return ['add_decisions', 'define_action_items'];

		default:
			// fallback: use generator.name for providers without a confirmed meta.key.
			if (generatorName === 'Zoom') {
				return ['extract_user_quotes', 'add_decisions', 'define_action_items'];
			}
			if (generatorName === 'Microsoft Teams' || generatorName === 'Google Chat') {
				return ['add_decisions', 'define_action_items'];
			}
			if (generatorName === 'MURAL') {
				return ['list_key_themes'];
			}
			if (generatorName === 'Notion') {
				return ['list_key_topics'];
			}
			if (generatorName === 'Smartsheet') {
				return ['extract_insights'];
			}
			return [];
	}
};

const getConfigItem = (actionKey: SmartLinkActionKey, smartlinkUrl: string) => {
	switch (actionKey) {
		case 'list_key_topics':
		case 'list_key_themes':
		case 'extract_insights':
		case 'extract_user_quotes':
		case 'add_related_work_items':
		case 'list_todos':
			return smartLinkExtractList({ smartlinkUrl, actionType: actionKey });
		case 'add_decisions':
		case 'define_action_items':
			return smartLinkExtractActions({ smartlinkUrl, actionType: actionKey });
		case 'summarize_changes':
			return smartLinkDraftContent({ smartlinkUrl, actionType: actionKey });
		default: {
			// exhaustiveness check — TypeScript will error here if a new SmartLinkActionKey
			// is added without a corresponding case above.
			const _: never = actionKey;
			return _;
		}
	}
};

// icon render functions for each smartlink action key
const ACTION_KEY_ICONS: Record<SmartLinkActionKey, () => React.ReactNode> = {
	list_key_topics: () => <ListBulletedIcon label="" size="small" />,
	list_key_themes: () => <ListBulletedIcon label="" size="small" />,
	extract_insights: () => <ChartTrendUpIcon label="" size="small" />,
	extract_user_quotes: () => <QuotationMarkIcon label="" size="small" />,
	add_decisions: () => <DecisionIcon label="" size="small" />,
	define_action_items: () => <ListChecklistIcon label="" size="small" />,
	summarize_changes: () => <ClockIcon label="" size="small" />,
	add_related_work_items: () => <ChildWorkItemsIcon label="" size="small" />,
	list_todos: () => <ListChecklistIcon label="" size="small" />,
};

export const getSmartLinkDropdownItems = ({
	aiGlobalOptIn,
	details,
	intl,
	nodeType,
	smartlinkUrl,
	testIdPrefix,
	triggeredForMark,
	view,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	details?: SmartLinkResponse;
	intl: IntlShape;
	nodeType: NodeType;
	smartlinkUrl: string;
	testIdPrefix: string;
	triggeredForMark?: MarkType;
	view: EditorView;
}): SmartLinkDropdownItem[] => {
	const clickHandler = (actionKey: SmartLinkActionKey) => {
		const openAIModalCommand = createOpenAIModalCommand({
			state: view.state,
			configItem: getConfigItem(actionKey, smartlinkUrl),
			lastTriggeredFrom: 'floatingToolbar',
			triggerMethod: 'click',
			aiGlobalOptIn,
			locale: intl.locale,
			triggeredFor: nodeType,
			triggeredForMark,
		});
		openAIModalCommand(view.state, view.dispatch, view);
	};

	const actionKeys = getActionsForSmartLink(details);

	// render action items for known providers; return empty for unknown ones.
	if (actionKeys.length > 0) {
		return actionKeys.map((actionKey) => ({
			icon: ACTION_KEY_ICONS[actionKey](),
			label: intl.formatMessage(actionLabels[actionKey]),
			testId: `${testIdPrefix}-smartlink-dropdown-${actionKey}`,
			onClick: () => clickHandler(actionKey),
		}));
	}

	// in future, if we decide to add generic actions for unknown providers, we can add them here
	return [];
};
